import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';

import './App.css';

// Components
import Sidebar from './components/Sidebar';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import SupplierList from './pages/SupplierList';
import BatchList from './pages/BatchList';
import BatchCreate from './pages/BatchCreate';
import BatchDetail from './pages/BatchDetail';
import Settings from './pages/Settings';
import PaymentSchedule from './components/PaymentSchedule';
import SupplierSummaryReport from './pages/reports/SupplierSummaryReport';
import CostCentreSummaryReport from './pages/reports/CostCentreSummaryReport';
import PaymentDetailReport from './pages/reports/PaymentDetailReport';
import MonthlyOverviewReport from './pages/reports/MonthlyOverviewReport';
import OutstandingPaymentsReport from './pages/reports/OutstandingPaymentsReport';
import AiChat from './components/AiChat';
import HelpCentre from './pages/HelpCentre';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

// Layout for authenticated pages
function AuthenticatedLayout() {
  return (
    <div className="app-layout">
      <Sidebar />
      <main className="main-content">
        <Outlet />
      </main>
      <AiChat />
    </div>
  );
}

// Protected Route wrapper
function ProtectedRoute() {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="loading-screen">
        Loading...
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  return <AuthenticatedLayout />;
}

function AppRoutes() {
  const { isAuthenticated } = useAuth();
  
  return (
    <Routes>
      {/* Public route */}
      <Route 
        path="/login" 
        element={isAuthenticated ? <Navigate to="/dashboard" /> : <Login />} 
      />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:uid/:token" element={<ResetPassword />} />
      
      {/* Protected routes */}
      <Route element={<ProtectedRoute />}>
        {/* Default redirect — Dashboard is now the landing page */}
        <Route index element={<Navigate to="/dashboard" replace />} />
        
        {/* Dashboard */}
        <Route path="/dashboard" element={<Dashboard />} />
        
        {/* Supplier routes */}
        <Route path="/suppliers" element={<SupplierList />} />
        
        {/* Payment/Batch routes */}
        <Route path="/batches" element={<BatchList />} />
        <Route path="/batches/new" element={<BatchCreate />} />
        <Route path="/batches/:id" element={<BatchDetail />} />
        <Route path="/batches/:id/edit" element={<BatchCreate />} />
        <Route path="/batches/:id/schedule" element={<PaymentSchedule />} />
        
        {/* Reporting routes */}
        <Route path="/reports/supplier-summary" element={<SupplierSummaryReport />} />
        <Route path="/reports/cost-centre-summary" element={<CostCentreSummaryReport />} />
        <Route path="/reports/payment-detail" element={<PaymentDetailReport />} />
        <Route path="/reports/monthly-overview" element={<MonthlyOverviewReport />} />
        <Route path="/reports/outstanding" element={<OutstandingPaymentsReport />} />
        
        {/* Settings */}
        <Route path="/settings" element={<Settings />} />

        {/* Help Centre */}
        <Route path="/help" element={<HelpCentre />} />
      </Route>
      
      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <AppRoutes />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;